import { Heading, Spinner } from "@ttc3k/trekker";
import { MultilingualLangKey } from "shared/types";
import { Flex } from "styled-system/jsx";
import { DateRange, EventWidgetSize, YearGroup } from "widgets/events/types";
import { useEventsWidgetContext } from "../EventsContext";
import { cva } from "styled-system/css";
import { MonthYearSelectorMobile } from "./MonthYearSelectorMobile";
import { MonthYearSelectorDesktop } from "./MonthYearSelectorDesktop";
import { useMonthYearSelector } from "./useMonthYearSelector";

const headingRecipe = cva({
  base: {
    textAlign: "center",
    minW: "184px",
  },
  variants: {
    size: {
      mobile: {
        minW: "140px",
      },
      tablet: {},
      desktop: {},
      large: {},
    },
  },
  defaultVariants: {
    size: "mobile",
  },
});

type MonthYearSelectorProps = {
  yearGroups: YearGroup[];
  firstMonthOfYearWithEvents: { [key: string]: string };
  dateRange: DateRange;
  lang: MultilingualLangKey;
  loading: boolean;
  loadingMonthsAndEventCounts: boolean;
  onDateRangeChange: (dateRange: DateRange) => void;
};

export const MonthYearSelector = ({
  yearGroups,
  firstMonthOfYearWithEvents,
  dateRange,
  lang,
  loading,
  loadingMonthsAndEventCounts,
  onDateRangeChange,
}: MonthYearSelectorProps) => {
  const { widgetHeaderSize } = useEventsWidgetContext();

  const classes = headingRecipe({ size: widgetHeaderSize });

  const {
    title,
    selectedYear,
    selectedMonth,
    handleYearClick,
    handleMonthClick,
    setSelectedYear,
  } = useMonthYearSelector({
    yearGroups,
    firstMonthOfYearWithEvents,
    dateRange,
    lang,
    onDateRangeChange,
  });

  if (loadingMonthsAndEventCounts) {
    return (
      <Flex
        w={"full"}
        h={"full"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <Spinner width={"24px"} height={"24px"} />
      </Flex>
    );
  }

  return dateRange.isFullMonth ? (
    widgetHeaderSize === EventWidgetSize.Mobile ? (
      <MonthYearSelectorMobile
        yearGroups={yearGroups}
        dateRange={dateRange}
        selectedMonth={selectedMonth}
        handleMonthClick={handleMonthClick}
        setSelectedYear={setSelectedYear}
      />
    ) : (
      <MonthYearSelectorDesktop
        yearGroups={yearGroups}
        dateRange={dateRange}
        loading={loading}
        selectedYear={selectedYear}
        selectedMonth={selectedMonth}
        handleYearClick={handleYearClick}
        handleMonthClick={handleMonthClick}
      />
    )
  ) : (
    <Heading
      part="date__month"
      size={widgetHeaderSize === EventWidgetSize.Mobile ? "h5" : "h4"}
      color="text.dark"
      className={classes}
    >
      {title}
    </Heading>
  );
};
